import { createStyles, makeStyles, Theme } from '@material-ui/core';
import Client from '@spike/client-model';
import clsx from 'clsx';
import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react';
import SidePanel from '../SidePanel';
import ChatHeader from './ChatHeader';
import ChatMessages from 'components/SMS/TwoWay/Chat/ChatMessages';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from 'store';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { createEmptySmsMessage, SmsMessage } from 'model/TwoWaySms';
import { TwoWaySmsStatus } from 'reducers/twowaysms/TwoWaySmsState';
import {
    clearTwoWaySmsMessagesThunk,
    fetchTwoWaySmsCountByCustomerThunk,
    fetchTwoWaySmsCountThunk,
    fetchTwoWaySmsMessagesThunk,
    saveMessageThunk,
    searchTwoWaySmsThunk
} from 'actions/twoWaySms/twoWaySmsActions';
import ChatInput from 'components/SMS/TwoWay/Chat/ChatInput';
import useNonInitialEffect from '@versiondos/hooks';
import NoPhoneNumber from './NoPhoneNumber';
import { useHasPremiumPlan } from 'hooks';
import FreePlan from './FreePlan';
import { getClientProfileOverviewThunk } from '@spike/clients-action';
import { useApiClientWrapper } from 'hooks';
import { requestProfileOverviewOnce } from 'components/Checkout2/requestProfileOverviewOnce';

interface TwoWaySMSDrawerProps {
    client: Client;
    onClose: () => void;
    onBack: () => void;
    onShowClient?: () => void;
    hide?: boolean;
    className?: string;
    hideBackdrop?: boolean;
    side?: 'left' | 'right';
    offset?: number;
    zIndex?: number;
    showCloseButton?: boolean;
}

const useStyles = makeStyles<Theme, Pick<TwoWaySMSDrawerProps, 'side' | 'offset'>>((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%'
        },
        transparentBackdrop: {
            backgroundColor: 'transparent'
        },
        offsetPaper: props => ({
            position: 'fixed',
            ...(props.side === 'right'
                ? { right: `${props.offset}px !important` }
                : { left: `${props.offset}px !important` }),

            [theme.breakpoints.down('md')]: {
                right: '0px !important',
                left: '0px !important'
            }
        }),
        noShadow: {
            boxShadow: 'none !important'
        },
        borderSeparatorRight: {
            borderRight: '1px solid #D4D4D4'
        },
        drawerSMSRoot: props => ({
            'width': 'calc(100vw - 590px) !important',

            '& .MuiDrawer-paper .sidepanel-close-button': {
                right: `${(props.offset || 0) + 590 + 30}px`
            }
        }),
        chatDisplayContainer: {
            width: '100%',
            maxWidth: '100%',
            flexGrow: 1
        },
        chatInputContainer: {
            width: '100%',
            zIndex: 99,
            borderTop: '#D4D4D4 1px solid'
        },
        drawerSMSBackdrop: {
            width: 'calc(100vw - 590px) !important'
        }
    })
);

const TwoWaySMSDrawer: FunctionComponent<TwoWaySMSDrawerProps> = ({
    client,
    onClose,
    onBack,
    onShowClient,
    className,
    hideBackdrop = false,
    showCloseButton = true,
    hide = false,
    side = 'right',
    offset = 0,
    zIndex = 99
}) => {
    const classes = useStyles({ side, offset });
    const chatScrollRef = useRef<OverlayScrollbarsComponent>(null);
    const dispatch = useDispatch();
    const hasPremiumPlan = useHasPremiumPlan();
    const apiClientWrapper = useApiClientWrapper();

    const marketplaceId = useSelector<RootState, number>(state => state.marketplace.marketplace.id!);
    const twoWayStatus = useSelector<RootState, TwoWaySmsStatus>(state => state.twoWaySms.status);
    const timeZone = useSelector<RootState, string>(state => state.login.auth.timeZone!);
    const messages = useSelector<RootState, Array<SmsMessage>>(state => state.twoWaySms.conversation ?? []);

    const [loading, setLoading] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [messageToSent, setMessageToSent] = useState<SmsMessage | undefined>(undefined);
    const [showMessages, setShowMessages] = useState(30);

    useEffect(() => {
        dispatch(clearTwoWaySmsMessagesThunk());
        if (client?.id) {
            dispatch(fetchTwoWaySmsMessagesThunk(client.id));
            requestProfileOverviewOnce(client.id, () => {
                dispatch(getClientProfileOverviewThunk(apiClientWrapper, client?.id!));
            });
        }
        return () => {
            dispatch(clearTwoWaySmsMessagesThunk());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (client && client.id) {
            setMessageToSent(createEmptySmsMessage(client.id));
        }
    }, [client]);

    useEffect(() => {
        if (!hide) {
            setTimeout(() => {
                scrollToBottomHandler(chatScrollRef);
                setMessagesAsRead(messages);
            }, 50);
        }
    }, [hide]);

    useNonInitialEffect(() => {
        if (!hide) {
            setMessagesAsRead(messages);
        }
    }, [messages, hide]);

    useNonInitialEffect(() => {
        if (twoWayStatus === TwoWaySmsStatus.SaveSuccess) {
            setMessageText('');
            if (client && client.id) {
                dispatch(fetchTwoWaySmsMessagesThunk(client.id));
                dispatch(fetchTwoWaySmsCountThunk());
                dispatch(fetchTwoWaySmsCountByCustomerThunk(client.id));
                dispatch(searchTwoWaySmsThunk('', 1, 30, 'all', 'newest'));
            }
        }

        if (twoWayStatus === TwoWaySmsStatus.FetchSuccess) {
            scrollToBottomHandler(chatScrollRef);
            setLoading(false);
        }
    }, [twoWayStatus]);

    const setMessagesAsRead = (messages: Array<SmsMessage>) => {
        const unReadMessages = messages.filter(message => !message.readAt && message.senderType === 'Customer');
        if (unReadMessages && unReadMessages.length > 0) {
            for (const message of unReadMessages) {
                message.readAt = new Date();
                dispatch(saveMessageThunk(message));
            }
            dispatch(fetchTwoWaySmsCountThunk());
            if (client && client.id) {
                dispatch(fetchTwoWaySmsMessagesThunk(client.id));
                dispatch(fetchTwoWaySmsCountByCustomerThunk(client.id));
            }
        }
    };

    const scrollHandler = () => {
        if (chatScrollRef.current) {
            const vp = chatScrollRef.current.osInstance()?.getElements().viewport;
            if (vp) {
                if (vp.scrollTop === 0) {
                    const newN = showMessages + 1;
                    setShowMessages(newN);
                    vp.scrollTop = vp.scrollTop + 20;
                }
            }
        }
        return null;
    };

    const sendMessageHandler = () => {
        if (messageToSent) {
            const staffId = store.getState().login.auth.user?.staffId;
            const messageToSentOther = { ...messageToSent };
            if (marketplaceId && marketplaceId !== 0) {
                messageToSentOther.marketplaceId = marketplaceId;
            }
            if (staffId) {
                messageToSentOther.senderId = staffId;
            }
            if (client.id) {
                messageToSentOther.clientId = client.id;
                messageToSentOther.receiverId = client.id;
            }
            messageToSentOther.sentAt = new Date();
            setMessageToSent(messageToSentOther);
            dispatch(saveMessageThunk(messageToSentOther));
        }
    };

    const changeMessageHandler = (text: string) => {
        if (messageToSent) {
            const messageToSentOther = { ...messageToSent };
            messageToSentOther.message = text;
            setMessageToSent(messageToSentOther);
            setMessageText(text);
        }
    };

    const scrollToBottomHandler = (ref: RefObject<OverlayScrollbarsComponent>) => {
        if (ref.current) {
            const vp = ref.current.osInstance()?.getElements().viewport;
            if (vp) {
                const scrollHeight = vp.scrollHeight;
                const height = vp.clientHeight;
                const maxScrollTop = scrollHeight - height;
                vp.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
            }
        }
    };

    return (
        <SidePanel
            side={side}
            hide={hide}
            showCloseButton={showCloseButton}
            onClose={onClose}
            hideBackdrop={hideBackdrop}
            className={clsx(classes.drawerSMSRoot)}
            classesInner={{
                paper: clsx({
                    [classes.offsetPaper]: offset > 0,
                    [classes.noShadow]: true,
                    [classes.borderSeparatorRight]: true
                })
            }}
            classesBackdrop={{
                classes: {
                    root: classes.drawerSMSBackdrop
                }
            }}
            zIndex={zIndex}
        >
            <div className={classes.root}>
                {hasPremiumPlan ? (
                    client.phone ? (
                        <>
                            <ChatHeader
                                client={client}
                                onBackButton={onBack}
                            />
                            <div className={classes.chatDisplayContainer}>
                                <ChatMessages
                                    clientData={client}
                                    loading={loading}
                                    messages={messages.slice(-showMessages)}
                                    timeZone={timeZone}
                                    chatScrollRef={chatScrollRef}
                                    onScroll={scrollHandler}
                                />
                            </div>
                            <div className={classes.chatInputContainer}>
                                <ChatInput
                                    isActive={!!client}
                                    messageText={messageText}
                                    onMessageChange={changeMessageHandler}
                                    onSendMessage={sendMessageHandler}
                                />
                            </div>
                        </>
                    ) : (
                        <NoPhoneNumber
                            onBackButton={onBack}
                            onShowClient={onShowClient}
                        />
                    )
                ) : (
                    <FreePlan onBackButton={onBack} />
                )}
            </div>
        </SidePanel>
    );
};

export default TwoWaySMSDrawer;
